import React, { useEffect, useState } from 'react';
import { AuditLogEventName, StyledDivider } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Stack, Typography } from '@mui/material';
import { IAuditLogItemResponse } from 'models/auditLog';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { ILicenceTemplateResponse } from 'models/licenceConfigurator';
import { ELicenceFormLabels } from 'pages/LicenceConfigurationPage/helper';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useNavigate } from 'react-router-dom';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { EAuditLogEvents } from '../helpers';
import { findInDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';

interface ILicencesEventDetails {
  auditLog: IAuditLogItemResponse;
}

interface IRenderDetails {
  licence: ILicenceTemplateResponse;
  t: any;
}
const RenderDetails = ({ licence, t }: IRenderDetails) => {
  const { weaponLicenceTemplateStatus, getWeaponLicenceTemplateStatus } = useDictionary();

  useEffect(() => {
    getWeaponLicenceTemplateStatus();
  }, []);

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(ELicenceFormLabels.statusUniqueId)}
          </Typography>
          <Typography variant="body2">
            {findInDictionary(licence?.statusUniqueId, weaponLicenceTemplateStatus)}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(ELicenceFormLabels.name)}
          </Typography>
          <Typography variant="body2">{licence?.general.name}</Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(ELicenceFormLabels.description)}
          </Typography>
          {getHtmlText(licence?.general.description)}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 6, lg: 12 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(ELicenceFormLabels.relatedLegalEntityType)}
          </Typography>
          <Typography variant="body2">
            {licence?.owner?.legalTypes?.length
              ? licence?.owner.legalTypes?.map(
                  (type, key) =>
                    `${t(`licence.userType.${type.toLowerCase()}.label`)}${
                      licence?.owner.legalTypes.length > key + 1 ? ', ' : ''
                    } `,
                )
              : '-'}
          </Typography>
        </Stack>
      </Grid>

      <StyledDivider />

      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Stack>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(ELicenceFormLabels.code)}
          </Typography>
          <Typography variant="body2">{emptyFieldHolder(licence?.general?.code)}</Typography>
        </Stack>
      </Grid>
      {licence?.general?.licenceDurationUnlimited ? (
        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t(ELicenceFormLabels.licenceDurationUnlimited)}
            </Typography>
            <Typography variant="body2">{t('general.yes.label')}</Typography>
          </Stack>
        </Grid>
      ) : (
        <>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(ELicenceFormLabels.licenceDurationYears)}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licence?.general?.licenceDurationYears)}
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(ELicenceFormLabels.licenceDurationMonths)}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licence?.general?.licenceDurationMonths)}
              </Typography>
            </Stack>
          </Grid>
        </>
      )}

      <StyledDivider />

      <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ paddingRight: 2.5 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.goodReason)}
        </Typography>
        {t(
          licence.conditions.goodReason
            ? `licence.goodReason.${licence.conditions.goodReason.toLocaleLowerCase()}.label`
            : 'general.no.label',
        )}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ paddingRight: 2.5 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.specificCountryArea)}
        </Typography>
        {licence.conditions.specificCountryArea
          ? t('licence.areaRestriction.national')
          : t('licence.areaRestriction.specificArea')}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.competency)}
        </Typography>
        {licence.conditions.competency ? t('general.yes.label') : t('general.no.label')}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ paddingRight: 2.5 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.safeStorage)}
        </Typography>
        {licence.conditions.safeStorage ? t('general.yes.label') : t('general.no.label')}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ paddingRight: 2.5 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.weaponCategory)}
        </Typography>
        {licence.conditions.weaponCategory ? t('general.yes.label') : t('general.no.label')}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ paddingRight: 2.5 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.weaponNumberLimit)}
        </Typography>
        {licence.conditions.weaponNumberLimit ? t('general.yes.label') : t('general.no.label')}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.carryConditions)}
        </Typography>
        {licence.conditions.carryConditions ? t('general.yes.label') : t('general.no.label')}
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(ELicenceFormLabels.additionalConditions)}
        </Typography>
        {licence.conditions.additionalConditions
          ? t('general.yes.label')
          : t('general.no.label')}
      </Grid>
    </>
  );
};

const LicencesEventDetails = ({ auditLog }: ILicencesEventDetails) => {
  const [licencesDetails, setLicencesDetails] = useState<ILicenceTemplateResponse | null>();
  const [previouslicencesDetails, setPreviousLicencesDetails] =
    useState<ILicenceTemplateResponse | null>();
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (auditLog && auditLog?.postObjectValue) {
        const postObjectValue = auditLog.postObjectValue as any;
        setLicencesDetails(postObjectValue);
      }
      if (auditLog && auditLog?.previousObjectValue) {
        const previousObjectValue = auditLog.previousObjectValue as any;
        setPreviousLicencesDetails(previousObjectValue);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditLog]);

  return (
    <>
      {previouslicencesDetails &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.PUBLISH_LICENCE_DEFINITION &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.UNPUBLISH_LICENCE_DEFINITION && (
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_LICENCE_DEFINITION
                ? `${t('licenceConfiguration.header')} ${t(
                    'auditLogDetails.before.event.title',
                  )}`
                : `${t('licenceConfiguration.header')} ${t(
                    'auditLogDetails.after.event.title',
                  )}`}
            </Typography>
          </AuditLogEventName>
        )}

      {previouslicencesDetails &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.PUBLISH_LICENCE_DEFINITION &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.UNPUBLISH_LICENCE_DEFINITION && (
          <DetailsTemplate
            title={previouslicencesDetails?.general.name}
            pageType={
              auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION
                ? EDetailsPageTypes.auditLogView
                : undefined
            }
            redirectToView={() =>
              navigate(
                `${ERouteLinks.LicenceTemplatePage.replace(
                  ':id',
                  previouslicencesDetails?.uniqueId,
                )}`,
              )
            }
          >
            <RenderDetails licence={previouslicencesDetails} t={t} />
          </DetailsTemplate>
        )}

      {auditLog &&
        auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION && (
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('licenceConfiguration.header')} ${t('auditLogDetails.post.event.title')}`}
            </Typography>
          </AuditLogEventName>
        )}
      {licencesDetails && (
        <DetailsTemplate
          title={licencesDetails?.general.name}
          pageType={
            auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_LICENCE_DEFINITION
              ? EDetailsPageTypes.auditLogView
              : undefined
          }
          redirectToView={() =>
            navigate(
              `${ERouteLinks.LicenceTemplatePage.replace(':id', licencesDetails?.uniqueId)}`,
            )
          }
        >
          <RenderDetails licence={licencesDetails} t={t} />
        </DetailsTemplate>
      )}
    </>
  );
};

export default LicencesEventDetails;
